import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid";
import { randomId } from "@mui/x-data-grid-generator";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  TextField,
  Box,
  Tooltip,
  Grid,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { NumericFormat } from "react-number-format";
import {
  DeleteOutlined as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CloudUpload as SyncIcon,
  ElectricalServicesSharp,
} from "@mui/icons-material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import CachedIcon from "@mui/icons-material/Cached";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import InvoiceDetailDialog from "../components/InvoiceDetailDialog";
import ConfirmDialog from "../components/ConfirmDialog";

import invoiceApi from "../api/invoiceApi";
import taxRateTypeApi from "../api/taxRateTypeApi";
import itemTypeApi from "../api/itemTypeApi";

import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "../css/Dialog.css";

// const productCategories = [
//   { value: 0, label: "Hàng hóa, dịch vụ" },
//   { value: 1, label: "Giảm giá" },
//   { value: 2, label: "Phí phục vụ" },
//   { value: 3, label: "Ghi chú" },
//   { value: 4, label: "Giảm thuế" },
// ];

// const taxTypes = [
//   { value: 1, label: "Chịu thuế" },
//   { value: 2, label: "Không chịu thuế" },
//   { value: 3, label: "Không kê khai thuế" },
// ];

function ItemDetailsToolbar(props) {
  const { itemRows, setItemRows } = props;

  const handleAddItem = () => {
    let id = randomId();
    const row = itemRows.find((x) => x.isNew === true);
    if (row) {
      id = row.id;
    } else {
      setItemRows((oldRows) => [
        ...oldRows,
        {
          id: id,
          ReidId: null,
          ReidPrdNo: "",
          ReidPrdName: "",
          ReidUnitName: "",
          ReidQuantity: 0,
          ReidUnitPrice: 0,
          ReidItemAmount: 0,
          ReidDiscountPercent: 0,
          ReidDiscountAmount: 0,
          ReidVatPercent: 0,
          ReidVatAmount: 0,
          ReidTotalAmount: 0,
          ReidProductType: 0,
          ReidTaxType: 1,
          isNew: true,
        },
      ]);
    }
  };
  return (
    <Toolbar sx={{ minHeight: 33 }}>
      <Tooltip title="Thêm hàng hoá, dịch vụ">
        <ToolbarButton
          color="primary"
          onClick={handleAddItem}
          sx={{ fontSize: 12 }}
        >
          <AddCircleOutlineOutlinedIcon /> Thêm hàng hoá, dịch vụ
        </ToolbarButton>
      </Tooltip>
    </Toolbar>
  );
}

const InvoiceModal = React.memo(({ open, onClose, initialData }) => {
  const user = useSelector((state) => state.auth.user);
  const [openRowEditModal, setOpenRowEditModal] = useState(false);
  const [uncalculated, setUncalculated] = useState(false);
  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    title: "",
    content: "",
    confirmText: "Chấp nhận",
    cancelText: "Hủy",
    loading: false,
    onConfirm: () => {},
    onClose: () => {},
  });
  const customTheme = useMemo(() => {
    return createTheme({
      typography: {
        fontSize: 12,
        h6: { fontSize: "1rem" },
        body1: { fontSize: "0.875rem" },
        body2: { fontSize: "0.75rem" },
        button: { fontSize: "0.75rem" },
        caption: { fontSize: "0.65rem" },
      },
      components: {
        MuiDataGrid: {
          styleOverrides: {
            cell: { fontSize: "11px", wordBreak: "break-word" },
            filter: { maxHeight: "33px !important" },
            columnHeader: {
              fontSize: "12px",
              minHeight: "36px !important",
              maxHeight: "36px !important",
              height: "36px !important",
            },
            row: {
              minHeight: "32px !important",
            },
            ".MuiDataGrid-virtualScrollerContent": {
              flexBasis: "32px",
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: { fontSize: "0.8rem" },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              fontSize: "0.85rem",
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: "0.8rem",
            },
          },
        },

        MuiInputBase: {
          styleOverrides: {
            root: {
              fontSize: "11px",
              padding: "0 !important",
            },
            input: {
              fontSize: "11px",
              padding: "6px 12px !important",
            },
            textarea: {
              fontSize: "11px",
              padding: "6px 12px !important",
            },
          },
        },
      },
    });
  }, []);

  const defaultData = useMemo(() => {
    return {
      ReiId: null,
      ReiInvoiceNum: "",
      ReiSign: "",
      ReiTemplate: "",
      ReiBuyerName: "",
      ReiCompanyName: "",
      ReiTaxId: "",
      ReiAddress: "",
      ReiPhone: "",
      ReiEmail: "",
      ReiCitizenIdentification: "",
      ReiPassportNumber: "",
      ReiBudgetaryRelationshipCode: "",
      ReiContent: "",
      ReiDescription: "",
      ReiItemAmount: 0,
      ReiDiscountPercent: 0,
      ReiDiscountAmount: 0,
      ReiAmountBeforeTax: 0,
      ReiVatAmount: 0,
      ReiAmountAfterTax: 0,
      ReiStatus: 1,
      ReiDate: dayjs(),
      Details: [],
      PaymentMethod: "TM",
      ReiParentId: null,
      ReiIsApproved: false,
      CusId: null,
      ReiRate: 1,
      CurName: "VND",
      ReiStatusName: "Mới tạo",
      ReiEStatus: 1,
      ReiEStatusName: "Mới tạo",
      ReiReason: "",
      TkCloseDate: null,
    };
  }, []);

  const [editData, setEditData] = useState(initialData || defaultData);
  const [itemRows, setItemRows] = React.useState(
    initialData
      ? initialData.Details.map((detail) => ({
          ...detail,
          id: detail.ReidId,
        }))
      : [],
  );

  const [isApprove, setIsApprove] = useState(false);
  const isReadOnlyRow = editData.ReiStatus === 1;
  const isReadOnlyMode =
    editData.ReiStatus === 1 && editData.TkCloseDate === null;
  const diaglogTitle = useCallback(() => {
    if (editData.ReiStatus === 1) {
      return "Xuất hóa đơn";
    } else if (editData.ReiStatus === 2) {
      return "Hoá đơn thay thế";
    } else if (editData.ReiStatus === 3) {
      return "Hoá đơn điều chỉnh thông tin";
    } else if (editData.ReiStatus === 4) {
      return "Hoá đơn điều chỉnh tăng";
    } else if (editData.ReiStatus === 5) {
      return "Hoá đơn điều chỉnh giảm";
    } else {
      return "Trạng thái không xác định";
    }
  }, [editData.ReiStatus]);
  const reiEStatusName = useCallback(() => {
    if (editData.ReiEStatus === 1) {
      let title = "Tạo mới";
      if (editData.ReiStatus === 1 && !editData.TkCloseDate)
        title = "Tạo mới (Phiếu chưa đóng)";
      return title;
    } else if (editData.ReiEStatus === 2) {
      return "Huỷ";
    } else if (editData.ReiEStatus === 3) {
      return "Đã đồng bộ";
    } else if (editData.ReiEStatus === 4) {
      return "Đã ký";
    }
  }, [editData.ReiEStatus, editData.TkCloseDate]);

  const [itemTypes, setItemTypes] = useState([]);
  const [taxRateTypes, setTaxRateTypes] = useState([]);
  const fetchTaxRateTypes = async () => {
    try {
      const response = await taxRateTypeApi.getAll();
      setTaxRateTypes(response.data);
    } catch (error) {
      console.error("Error fetching tax rate types:", error);
    }
  };

  const fetchItemTypes = async () => {
    try {
      const response = await itemTypeApi.getAll();
      setItemTypes(response.data);
    } catch (error) {
      console.error("Error fetching item types:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTaxRateTypes();
      fetchItemTypes();
      const dataToSet = initialData
        ? {
            ...defaultData,
            ...initialData,
            ReiDate: initialData.ReiDate ? dayjs(initialData.ReiDate) : dayjs(),
            ReiItemAmount: initialData.ReiItemAmount || 0,
            ReiDiscountPercent: initialData.ReiDiscountPercent || 0,
            ReiDiscountAmount: initialData.ReiDiscountAmount || 0,
            ReiAmountBeforeTax: initialData.ReiAmountBeforeTax || 0,
            ReiVatAmount: initialData.ReiVatAmount || 0,
            ReiAmountAfterTax: initialData.ReiAmountAfterTax || 0,
          }
        : defaultData;
      setEditData(dataToSet);
      setItemRows(
        dataToSet.Details.map((detail) => ({
          ...detail,
          id: detail.ReidId === 0 ? randomId() : detail.ReidId || randomId(), // Sử dụng ReidId hoặc randomId
        })),
      );
      setIsApprove(dataToSet.ReiStatus === 1 && dataToSet.ReiIsApproved);
      setUncalculated(false);
      if (dataToSet.ReiId === null && dataToSet.ReiParentId !== null) {
        calculateTotalsFromData(dataToSet.Details);
      }
    }
  }, [open, initialData, defaultData]);

  const calculateTotalsFromData = (data) => {
    const totalItemAmount = data
      .filter((x) => x.ReidProductType === 0 || x.ReidProductType === 2)
      .reduce((sum, item) => sum + (item.ReidItemAmount || 0), 0);

    const totalDiscountItemAmount = data.reduce((sum, item) => {
      if (item.ReidProductType === 1 || item.ReidProductType === 4) {
        return sum + Math.abs(item.ReidItemAmount || 0);
      }
      return sum + (item.ReidDiscountAmount || 0);
    }, 0);
    const amountBeforeTax = totalItemAmount - totalDiscountItemAmount;
    const reiVatAmount = data.reduce(
      (sum, item) => sum + (item.ReidVatAmount || 0),
      0,
    );
    const amountAfterTax = amountBeforeTax + reiVatAmount;

    setEditData((prev) => ({
      ...prev,
      ReiItemAmount: totalItemAmount,
      ReiDiscountAmount: totalDiscountItemAmount,
      ReiAmountBeforeTax: amountBeforeTax,
      ReiVatAmount: reiVatAmount,
      ReiAmountAfterTax: amountAfterTax,
    }));
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    setEditData((prev) => ({ ...prev, [name]: value }));
  };
  const handleSearchTaxInfor = async () => {
    if (
      editData.ReiTaxId === null ||
      editData.ReiTaxId === undefined ||
      editData.ReiTaxId === ""
    )
      return;
    try {
      const response = await invoiceApi.SearchTaxInfor(editData.ReiTaxId);
      if (response.success && response.data) {
        setEditData((prev) => ({
          ...prev,
          ReiCompanyName: response.data.BuyerCompanyName,
          ReiAddress: response.data.BuyerAddress,
          ReiBuyerName: "",
        }));
      } else {
        toast.warning(
          "Không tìm thấy thông tin của mã số thuế này. Vui lòng kiểm tra lại.",
        );
      }
    } catch (error) {
      console.error("Đã xảy ra lỗi khi tìm thông tin: ", error);
      toast.error(error.toString() || "Đã xảy ra lỗi khi tìm thông tin.");
    } finally {
    }
  };

  const handleDeleteClick = (id) => () => {
    if (isReadOnlyRow) return;
    let rows = itemRows.filter((row) => row.id !== id);
    setItemRows(rows);
    calculateTotalsFromData(rows);
  };

  const processRowUpdate = (newRow, oldRow) => {
    if (isReadOnlyRow) return newRow;
    let updatedRow = { ...newRow, isNew: undefined };
    const editedField = Object.keys(newRow).find(
      (key) => newRow[key] !== oldRow[key],
    );
    if (!editedField) return newRow;
    if (uncalculated === false) {
      const quantity = newRow.ReidQuantity || 0;
      const unitPrice = newRow.ReidUnitPrice || 0;
      const itemAmount = quantity * unitPrice;
      let itemDiscountPercent = newRow.ReidDiscountPercent || 0;
      let itemVatPercent = newRow.ReidVatPercent || 0;

      let itemDiscountAmount = newRow.ReidDiscountAmount || 0;
      if (editedField === "ReidDiscountAmount")
        itemDiscountPercent = (itemDiscountAmount * 100) / itemAmount;
      else {
        itemDiscountAmount = (itemAmount * itemDiscountPercent) / 100;
      }

      const amountAfterItemDiscount = itemAmount - itemDiscountAmount;

      let itemVatAmount = newRow.ReidVatAmount || 0;
      if (editedField === "ReidVatAmount")
        itemVatPercent = (itemVatAmount * 100) / amountAfterItemDiscount;
      else {
        itemVatAmount = (amountAfterItemDiscount * itemVatPercent) / 100;
      }

      let itemTotalAmount = amountAfterItemDiscount + itemVatAmount;
      updatedRow = {
        ...newRow,
        ReidItemAmount: itemAmount,
        ReidDiscountPercent: itemDiscountPercent,
        ReidDiscountAmount: itemDiscountAmount,
        ReidVatPercent: itemVatPercent,
        ReidVatAmount: itemVatAmount,
        ReidTotalAmount: itemTotalAmount,
        isNew: undefined,
      };
    }
    const newItems = itemRows.map((row) =>
      row.id === newRow.id ? updatedRow : row,
    );
    setItemRows(newItems);
    if (uncalculated === false) calculateTotalsFromData(newItems);
    return updatedRow;
  };
  const columns = [
    {
      field: "ReidProductType",
      headerName: "Loại hàng hoá",
      headerAlign: "center",
      width: 140,
      editable: !isReadOnlyRow,
      type: "singleSelect",
      align: "center",
      valueOptions: itemTypes.map((type) => ({
        value: type.itemTypeId,
        label: type.itemTypeName,
      })),
    },
    {
      field: "ReidPrdNo",
      headerName: "Mã hàng",
      headerAlign: "center",
      width: 90,
      editable: !isReadOnlyRow,
    },
    {
      field: "ReidPrdName",
      headerName: "Tên hàng hoá/Dịch vụ",
      headerAlign: "center",
      minWidth: 200,
      flex: 1,
      editable: !isReadOnlyRow,
    },
    {
      field: "ReidUnitName",
      headerName: "ĐVT",
      headerAlign: "center",
      align: "center",
      width: 70,
      editable: !isReadOnlyRow,
    },
    {
      field: "ReidQuantity",
      headerName: "Số lượng",
      headerAlign: "center",
      type: "number",
      width: 80,
      editable: !isReadOnlyRow,
      valueFormatter: (params) =>
        params?.toLocaleString("vi-VN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 10,
        }) || 0,
      align: "right",
    },
    {
      field: "ReidUnitPrice",
      headerName: "Đơn giá",
      headerAlign: "center",
      type: "number",
      width: 100,
      editable: !isReadOnlyRow,
      valueFormatter: (params) =>
        params?.toLocaleString("vi-VN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 10,
        }) || 0,
      align: "right",
    },
    {
      field: "ReidItemAmount",
      headerName: "Thành tiền",
      headerAlign: "center",
      type: "number",
      width: 100,
      editable: !isReadOnlyRow,
      valueFormatter: (params) =>
        params?.toLocaleString("vi-VN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 10,
        }) || 0,
      align: "right",
    },
    {
      field: "ReidDiscountPercent",
      headerName: "% Giảm giá",
      headerAlign: "center",
      type: "number",
      width: 90,
      editable: !isReadOnlyRow,
      valueFormatter: (params) =>
        params?.toLocaleString("vi-VN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 10,
        }) || 0,
      align: "right",
    },
    {
      field: "ReidDiscountAmount",
      headerName: "Giảm giá",
      headerAlign: "center",
      type: "number",
      width: 100,
      editable: !isReadOnlyRow,
      valueFormatter: (params) =>
        params?.toLocaleString("vi-VN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 10,
        }) || 0,
      align: "right",
    },
    {
      field: "ReidTaxType",
      headerName: "Loại thuế suất",
      headerAlign: "center",
      width: 110,
      editable: !isReadOnlyRow,
      type: "singleSelect",
      align: "center",
      valueOptions: taxRateTypes.map((type) => ({
        value: type.typeId,
        label: type.typeCode,
      })),
    },
    {
      field: "ReidVatPercent",
      headerName: "% Thuế",
      headerAlign: "center",
      type: "number",
      width: 80,
      editable: !isReadOnlyRow,
      valueFormatter: (params) =>
        params?.toLocaleString("vi-VN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 10,
        }) || 0,
      align: "right",
    },
    {
      field: "ReidVatAmount",
      headerName: "Tiền thuế",
      headerAlign: "center",
      type: "number",
      width: 90,
      editable: !isReadOnlyRow,
      valueFormatter: (params) =>
        params?.toLocaleString("vi-VN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 10,
        }) || 0,
      align: "right",
    },
    {
      field: "ReidTotalAmount",
      headerName: "Tổng tiền",
      headerAlign: "center",
      type: "number",
      width: 100,
      editable: !isReadOnlyRow,
      valueFormatter: (params) =>
        params?.toLocaleString("vi-VN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 10,
        }) || 0,
      align: "right",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Chức năng",
      headerAlign: "center",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        if (isReadOnlyRow) {
          return [];
        } else {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Xóa"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];
        }
      },
    },
  ];

  const handleSaveInvoice = async (isLock) => {
    if (itemRows.length === 0) {
      toast.error("Hóa đơn phải có ít nhất một mặt hàng.");
      return;
    }
    if (editData?.ReiEStatus === 4) {
      toast.error("Hóa đơn đã ký không thể chỉnh sửa.");
      return;
    }

    const trimmedEditData = { ...editData };
    Object.keys(trimmedEditData).forEach((key) => {
      if (typeof trimmedEditData[key] === "string") {
        trimmedEditData[key] = trimmedEditData[key].trim();
      }
    });
    // a. Kiểm tra Mã số thuế (10 số doanh nghiệp hoặc 13 số có gạch nối cho chi nhánh)
    if (trimmedEditData?.ReiTaxId) {
      // Chỉ cho phép nhập số và dấu '-', tổng độ dài từ 10 đến 16 ký tự
      const taxRegex = /^[0-9-]{10,16}$/;

      if (!taxRegex.test(trimmedEditData.ReiTaxId)) {
        toast.error("Vui lòng kiểm tra lại mã số thuế.");
        return;
      }
    }

    // b. Kiểm tra Số điện thoại (Đầu số VN thông dụng, 10 chữ số)
    if (trimmedEditData?.ReiPhone) {
      const phoneRegex = /^0[0-9]{9,10}$/;
      if (!phoneRegex.test(trimmedEditData.ReiPhone)) {
        toast.error("Số điện thoại không hợp lệ.");
        return;
      }
    }

    // d. Kiểm tra Căn cước công dân (CCCD mới là 12 chữ số)
    if (trimmedEditData?.ReiCitizenIdentification) {
      const idCardRegex = /^[0-9]{12}$/;
      if (!idCardRegex.test(trimmedEditData.ReiCitizenIdentification)) {
        toast.error("Căn cước công dân phải bao gồm đúng 12 chữ số.");
        return;
      }
    }

    // f. Kiểm tra Mã đơn vị quan hệ ngân sách (Chuẩn Bộ Tài Chính thường là 7 chữ số)
    if (trimmedEditData?.ReiBudgetaryRelationshipCode) {
      const budgetRegex = /^[0-9]{7}$/;
      if (!budgetRegex.test(trimmedEditData.ReiBudgetaryRelationshipCode)) {
        toast.error("Mã đơn vị quan hệ ngân sách phải bao gồm đúng 7 chữ số.");
        return;
      }
    }

    if (trimmedEditData?.ReiEmail) {
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      const emails = trimmedEditData.ReiEmail.split(",")
        .map((email) => email.trim())
        .filter(Boolean);

      const invalidEmail = emails.find((email) => !emailRegex.test(email));

      if (invalidEmail) {
        toast.error(`Email không hợp lệ: ${invalidEmail}`);
        return;
      }
    }
    try {
      const saveData = {
        ...trimmedEditData,
        ReiDate: trimmedEditData?.ReiDate
          ? trimmedEditData.ReiDate.toISOString()
          : null,
        Details: itemRows.map((item) => ({
          ...item,
          id: undefined,
          isNew: undefined,
        })),
      };
      const response = await invoiceApi.upsertInvoice(saveData, isLock);
      if (response.success) {
        toast.success("Thành công");
        setEditData((prev) => ({
          ...response.data,
          ReiDate: dayjs(response.data.ReiDate),
        }));
        setIsApprove(response.data.ReiIsApproved);
      } else {
        toast.error(response.message || "Lỗi khi lưu hóa đơn.");
      }
    } catch (error) {
      toast.error(error);
    } finally {
    }
  };
  const handleApproveInvoice = async (isApprove, step, currentData = null) => {
    try {
      const data = currentData || editData;
      if (step === 0) {
        if (data.ReiEStatus === 3) {
          const freshRawData = await handleGetInvoiceInfor(true);
          if (freshRawData) {
            // Chuẩn hóa data trả về giống hệt cấu trúc được lưu trong setEditData
            const freshData = {
              ...freshRawData,
              ReiDate: freshRawData.ReiDate
                ? dayjs(freshRawData.ReiDate)
                : dayjs(),
            };

            // Gọi đệ quy và truyền dữ liệu mới nhất vào
            handleApproveInvoice(isApprove, 0, freshData);
          } else {
            toast.error("Không thể tải lại thông tin hóa đơn.");
          }
          return;
        }
        if (data.ReiEStatus === 4) {
          setConfirmDialogState({
            open: true,
            title: "Mở khóa hóa đơn",
            content:
              "Hóa đơn này đã ký, mở khóa sẽ cho phép POS bán hàng được gọi lại phiếu. Bạn có muốn mở khóa không?",
            onConfirm: () => {
              handleApproveInvoice(isApprove, 1, data);
              setConfirmDialogState({
                ...confirmDialogState,
                open: false,
              });
            },
            onClose: () => {
              setConfirmDialogState({
                ...confirmDialogState,
                open: false,
              });
            },
          });
          return;
        }
      }

      const result = await invoiceApi.approveInvoice(data.ReiId, isApprove);

      if (result.success) {
        setIsApprove(isApprove);
        toast.success("Thành công");
      } else {
        toast.error(result.message || "Lỗi xử lý dữ liệu.");
      }
    } catch (error) {
      toast.error(error);
    } finally {
    }
  };

  const handleSyncInvoice = async () => {
    if (editData.ReiEStatus === 4) {
      toast.error("Hóa đơn đã ký không thể chỉnh sửa.");
      return;
    }
    try {
      let isSuccess = false;
      const response = await invoiceApi.upsertInvoice(editData, true);
      if (response.success) {
        isSuccess = true;
      } else {
        toast.error(response.message || "Lỗi khi lưu hóa đơn.");
      }
      if (isSuccess) {
        const response2 = await invoiceApi.syncInvoice(response.data.ReiId);
        if (response2.success) {
          toast.success("Đồng bộ hóa đơn thành công!");
        } else {
          toast.error(response2.message || "Lỗi khi đồng bộ hóa đơn.");
        }
        await loadInvoiceData(editData.ReiId);
      }
    } catch (error) {
      console.error("Lỗi khi đồng bộ hóa đơn:", error);
      toast.error(error.message || "Đã xảy ra lỗi khi đồng bộ hóa đơn.");
    } finally {
    }
  };

  const handleGetInvoiceInfor = async (isReturn) => {
    try {
      const result = await invoiceApi.getInvoiceInfor(editData.ReiId);
      if (result.success) {
        setEditData((prev) => ({
          ...result.data,
          ReiDate: result.data.ReiDate ? dayjs(result.data.ReiDate) : dayjs(),
        }));
        if (isReturn) return result.data;
        else {
          toast.success("Đã lấy thông tin");
          return null;
        }
      } else {
        if (isReturn) return null;
        else {
          toast.error(result.message);
          return null;
        }
      }
    } catch (error) {
      if (isReturn) return null;
      else {
        toast.error(error);
        return null;
      }
    } finally {
    }
    return null;
  };

  const loadInvoiceData = async (reiId) => {
    try {
      const invoiceData = await invoiceApi.getInvoiceData(reiId);
      if (invoiceData?.success && invoiceData?.data) {
        const dataToSet = {
          ...defaultData,
          ...invoiceData.data,
          ReiDate: invoiceData.data.ReiDate
            ? dayjs(invoiceData.data.ReiDate)
            : dayjs(),
          ReiItemAmount: invoiceData.data.ReiItemAmount || 0,
          ReiDiscountPercent: invoiceData.data.ReiDiscountPercent || 0,
          ReiDiscountAmount: invoiceData.data.ReiDiscountAmount || 0,
          ReiAmountBeforeTax: invoiceData.data.ReiAmountBeforeTax || 0,
          ReiVatAmount: invoiceData.data.ReiVatAmount || 0,
          ReiAmountAfterTax: invoiceData.data.ReiAmountAfterTax || 0,
        };
        setEditData(dataToSet);
        if (dataToSet.Details) {
          setItemRows(
            dataToSet.Details.map((detail) => ({
              ...detail,
              id:
                detail.ReidId === 0
                  ? randomId()
                  : detail.ReidId || randomId(),
            })),
          );
        }
        setIsApprove(dataToSet.ReiStatus === 1 && dataToSet.ReiIsApproved);
        setUncalculated(false);
        return dataToSet;
      }
    } catch (error) {
      console.error("Lỗi khi tải lại dữ liệu hóa đơn:", error);
    }
    return null;
  };

  const handleClearTaxAuthorityCode = (reiId) => {
    if (!reiId) {
      toast.warning("Không tìm thấy thông tin hóa đơn.");
      return;
    }
    setConfirmDialogState({
      open: true,
      title: "Xác nhận",
      content: "Bạn có chắc chắn muốn xóa mã CQT không?",
      confirmText: "Chấp nhận",
      cancelText: "Hủy",
      loading: false,
      onClose: () => {
        setConfirmDialogState((prev) => ({ ...prev, open: false }));
      },
      onConfirm: async () => {
        try {
          setConfirmDialogState((prev) => ({ ...prev, loading: true }));
          const response = await invoiceApi.clearTaxAuthorityCode(reiId);
          if (response?.success) {
            toast.success(response.message || "Xóa mã CQT thành công.");
            setConfirmDialogState((prev) => ({
              ...prev,
              open: false,
              loading: false,
            }));
            await loadInvoiceData(reiId);
          } else {
            toast.error(response?.message || "Lỗi khi xóa mã CQT.");
            setConfirmDialogState((prev) => ({ ...prev, loading: false }));
          }
        } catch (error) {
          toast.error(
            error?.message ||
              (typeof error === "string"
                ? error
                : "Đã xảy ra lỗi khi xóa mã CQT."),
          );
          setConfirmDialogState((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  return (
    <>
      <ThemeProvider theme={customTheme}>
        <Dialog open={open} fullWidth maxWidth="xl" className="ui-dialog">
          <DialogTitle className="ui-dialog-titlebar">
            {diaglogTitle()}
          </DialogTitle>
          <Tooltip title="Đóng">
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={(theme) => ({
                position: "absolute",
                right: 2,
                top: 2,
                color: "white",
              })}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <div className="ui-dialog-content">
            <Grid container sx={{ display: "flex" }} size={{ xs: 12, md: 12 }}>
              <Grid
                component="fieldset"
                //size={{ xs: 12, md: 12, lg: 3.92 }}
                sx={{ borderRadius: 3, flex: 1 }}
              >
                <Typography component="legend" variant="h6" align="left">
                  Thông tin hóa đơn
                </Typography>
                <Grid container spacing={0.6} alignItems="center">
                  <Grid
                    size={{
                      xs: 12,
                      md: 12,
                    }}
                    sx={{ minHeight: 26 }}
                  >
                    <Box>
                      <Typography
                        variant="h7"
                        sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                      >
                        Trạng thái HĐ:
                      </Typography>
                      <Typography variant="h7">{reiEStatusName()}</Typography>
                    </Box>
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 12 }}
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Grid
                      size={{ xs: 12, md: 7.5 }}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography
                        variant="h7"
                        sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                      >
                        Ký hiệu HĐ:
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Ký hiệu hoá đơn"
                        name="ReiSign"
                        value={editData.ReiSign}
                        onChange={handleFieldChange}
                        size="small"
                        slotProps={{
                          input: { readOnly: true },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        required
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 4.5 }}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography
                        variant="h7"
                        sx={{ minWidth: 60, textAlign: "end", mr: 1 }}
                      >
                        Mẫu HĐ:
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Mẫu hoá đơn"
                        name="ReiTemplate"
                        value={editData.ReiTemplate}
                        onChange={handleFieldChange}
                        size="small"
                        slotProps={{
                          input: { readOnly: true },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 12 }}
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Grid size={{ xs: 12, md: 7.5 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        mb={0}
                        sx={{ flex: 1 }}
                      >
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                        >
                          Ngày HĐ:
                        </Typography>
                        <DatePicker
                          placeholder="Ngày hóa đơn"
                          name="ReiDate"
                          format="DD/MM/YYYY"
                          value={editData.ReiDate}
                          onChange={(newValue) =>
                            setEditData((prev) => ({
                              ...prev,
                              ReiDate: newValue,
                            }))
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              inputProps: { readOnly: isReadOnlyMode },
                              InputLabelProps: { shrink: true },
                            },
                          }}
                          required
                          sx={{
                            "& .MuiPickersOutlinedInput-root": {
                              height: 28,
                              fontSize: 11,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4.5 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        mb={0}
                        sx={{ flex: 1 }}
                      >
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 60, textAlign: "end", mr: 1 }}
                        >
                          Số HĐ:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Số HĐ"
                          name="ReiInvoiceNum"
                          value={editData.ReiInvoiceNum}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: true },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      mb={0}
                      sx={{ flex: 1 }}
                    >
                      <Typography
                        variant="h7"
                        sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                      >
                        Mã CQT:
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Mã CQT"
                        name="ReiTaxAuthorityCode"
                        value={editData.ReiTaxAuthorityCode}
                        onChange={handleFieldChange}
                        size="small"
                        slotProps={{
                          input: { readOnly: true },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        sx={{ mb: 0.2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box display="flex" alignItems="center" mb={0}>
                      <Typography
                        variant="h7"
                        sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                      >
                        HTTT:
                      </Typography>
                      <TextField
                        fullWidth
                        name="PaymentMethod"
                        value={editData.PaymentMethod || ""}
                        onChange={handleFieldChange}
                        size="small"
                        required
                        slotProps={{
                          input: { readOnly: isReadOnlyMode },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      ></TextField>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                component="fieldset"
                //size={{ xs: 12, md: 12, lg: 8 }}
                sx={{ borderRadius: 3, flex: 2 }}
              >
                <Typography component="legend" variant="h6" align="left">
                  Thông tin người mua
                </Typography>
                <Grid
                  container
                  spacing={0.6}
                  alignItems="center"
                  sx={{ alignItems: "flex-start" }}
                >
                  <Grid
                    container
                    spacing={0.6}
                    sx={{ alignItems: "flex-start", width: "65%" }}
                  >
                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 80, textAlign: "end", mr: 1 }}
                        >
                          Mã số thuế:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Mã số thuế"
                          name="ReiTaxId"
                          value={editData.ReiTaxId || ""}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          sx={{ mr: 0.5 }}
                        />
                        <Button
                          onClick={handleSearchTaxInfor}
                          color="primary"
                          variant="contained"
                          size="small"
                          sx={{
                            width: 140,
                            minWidth: 140,
                            maxWidth: 140,
                            flexShrink: 0,
                            maxHeight: 28,
                          }}
                        >
                          Lấy thông tin
                        </Button>
                      </Box>
                    </Grid>
                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ height: "100%" }}
                      >
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 80, textAlign: "end", mr: 1 }}
                        >
                          Tên đơn vị:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Tên đơn vị"
                          name="ReiCompanyName"
                          value={editData.ReiCompanyName || ""}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: {
                              readOnly: isReadOnlyMode,
                              style: { height: "100%" },
                            },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          sx={{ height: "100%" }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ sx: 12, md: 12 }} sx={{ maxHeight: 60 }}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        sx={{ maxHeight: 60 }}
                      >
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 80, textAlign: "end", mr: 1 }}
                        >
                          Địa chỉ:
                        </Typography>
                        <TextField
                          multiline
                          rows={2}
                          fullWidth
                          placeholder="Địa chỉ"
                          name="ReiAddress"
                          size="small"
                          value={editData.ReiAddress || ""}
                          onChange={handleFieldChange}
                          slotProps={{
                            input: {
                              readOnly: isReadOnlyMode,
                              fontSize: "11px",
                            },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          sx={{
                            // Chiều cao tổng thể ~60px
                            "& .MuiOutlinedInput-root": {
                              height: 60,
                            },
                            // Textarea bên trong
                            "& .MuiInputBase-inputMultiline": {
                              fontSize: 11,
                              lineHeight: "1.3",
                              height: "50px !important",
                              overflow: "auto",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box display="flex" alignItems="center" mb={0}>
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 80, textAlign: "end", mr: 1 }}
                        >
                          Email:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Email"
                          name="ReiEmail"
                          value={editData.ReiEmail || ""}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        mb={0}
                        sx={{ flex: 1 }}
                      >
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 80, textAlign: "end", mr: 1 }}
                        >
                          Ghi chú:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Ghi chú"
                          name="ReiContent"
                          value={editData.ReiContent || ""}
                          onChange={handleFieldChange}
                          multiline
                          rows={2}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={0.6}
                    sx={{ alignItems: "flex-start", width: "34%" }}
                  >
                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                        >
                          Người mua:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Tên người mua"
                          name="ReiBuyerName"
                          value={editData.ReiBuyerName}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box display="flex" alignItems="center" mb={0}>
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                        >
                          Số CCCD:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Số căn cước công dân"
                          name="ReiCitizenIdentification"
                          value={editData.ReiCitizenIdentification || ""}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box display="flex" alignItems="center" mb={0}>
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                        >
                          Số hộ chiếu:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Số hộ chiếu"
                          name="ReiPassportNumber"
                          value={editData.ReiPassportNumber}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box display="flex" alignItems="center" mb={0}>
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                        >
                          SĐT:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Số điện thoại"
                          type="tel"
                          name="ReiPhone"
                          value={editData.ReiPhone || ""}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ sx: 12, md: 12 }}>
                      <Box display="flex" alignItems="center" mb={0}>
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                        >
                          Mã QHNS:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Mã quan hệ ngân sách"
                          name="ReiBudgetaryRelationshipCode"
                          value={editData.ReiBudgetaryRelationshipCode}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        sx={{ flex: 1 }}
                      >
                        <Typography
                          variant="h7"
                          sx={{ minWidth: 90, textAlign: "end", mr: 1 }}
                        >
                          Ghi chú phiếu:
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Ghi chú phiếu"
                          name="ReiDescription"
                          value={editData.ReiDescription || ""}
                          onChange={handleFieldChange}
                          size="small"
                          slotProps={{
                            input: { readOnly: isReadOnlyMode },
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Box sx={{ flexGrow: 1, width: "100%", mt: 1 }}>
              {editData.ReiStatus !== 1 && (
                <>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      alignContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        alignContent: "center",
                      }}
                    >
                      {editData.ReiStatus === 2
                        ? `Thay thế cho hóa đơn Mẫu số ${
                            editData?.ReiTemplateParent
                          }, ký hiệu ${
                            editData?.ReiSignParent
                          }, số ${editData?.ReiInvoiceNumParent}, ngày ${dayjs(
                            editData?.ReiDateParent,
                          )
                            .date()
                            .toString()} tháng ${(
                            dayjs(editData?.ReiDateParent).month() + 1
                          ).toString()} năm ${dayjs(editData?.ReiDateParent)
                            .year()
                            .toString()}.`
                        : `Điều chỉnh cho hóa đơn Mẫu số ${
                            editData?.ReiTemplateParent
                          } , ký hiệu ${
                            editData?.ReiSignParent
                          } , số ${editData?.ReiInvoiceNumParent.toString()} , ngày ${dayjs(
                            editData?.ReiDateParent,
                          )
                            .date()
                            .toString()} tháng ${(
                            dayjs(editData?.ReiDateParent).month() + 1
                          ).toString()} năm ${dayjs(editData?.ReiDateParent)
                            .year()
                            .toString()}.`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      alignContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: 120,
                        alignContent: "center",
                      }}
                    >
                      {editData.ReiStatus === 2
                        ? "Lý do thay thế: "
                        : "Lý do điều chỉnh: "}
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Lý do"
                      fullWidth
                      name="ReiReason"
                      value={editData?.ReiReason}
                      onChange={handleFieldChange}
                    ></TextField>
                  </Box>
                </>
              )}
              <Box sx={{ display: "flex", justifyContent: "left" }}>
                <Typography
                  variant="h5"
                  sx={{ mb: 0, fontWeight: "bold", alignContent: "center" }}
                >
                  Chi tiết
                </Typography>
                <Box sx={{ mb: 0, display: "flex" }}>
                  {editData.ReiStatus !== 1 && (
                    <>
                      <FormControlLabel
                        sx={{ ml: 2 }}
                        control={
                          <Checkbox
                            checked={uncalculated}
                            onChange={(e) => {
                              setUncalculated(e.target.checked);
                            }}
                          />
                        }
                        label="Không tự động tính toán"
                        size="small"
                      />
                    </>
                  )}
                </Box>
              </Box>

              <DataGrid
                rows={itemRows}
                columns={columns}
                editMode="cell"
                processRowUpdate={processRowUpdate}
                slots={{
                  toolbar: ItemDetailsToolbar,
                }}
                slotProps={{
                  toolbar: {
                    itemRows,
                    setItemRows,
                  },
                }}
                showToolbar={!isReadOnlyRow}
                hideFooter
                density="compact"
                disableColumnMenu
                sx={{
                  fontSize: 8,
                  height: 300,
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
                getRowHeight={() => "auto"}
                showCellVerticalBorder
                showColumnVerticalBorder
              />
              <Box sx={{ width: "100%" }}>
                <Grid container spacing={0.8} alignItems="center">
                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowNegative
                      fullWidth
                      margin="dense"
                      label="Thành tiền"
                      name="ReiItemAmount"
                      value={editData?.ReiItemAmount}
                      onValueChange={(values) => {
                        handleFieldChange({
                          target: {
                            name: "ReiItemAmount",
                            value: values.value,
                          },
                        });
                      }}
                      slotProps={{
                        input: {
                          readOnly: editData?.ReiStatus === 1 || !uncalculated,
                        },
                        uncalculated,
                      }}
                      sx={{
                        "& .MuiInputBase-input": { textAlign: "right" },
                      }}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowNegative
                      fullWidth
                      margin="dense"
                      label="Giảm giá"
                      name="ReiDiscountAmount"
                      value={editData?.ReiDiscountAmount}
                      onValueChange={(values) => {
                        handleFieldChange({
                          target: {
                            name: "ReiDiscountAmount",
                            value: values.value,
                          },
                        });
                      }}
                      slotProps={{
                        input: {
                          readOnly: editData?.ReiStatus === 1 || !uncalculated,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": { textAlign: "right" },
                      }}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowNegative
                      fullWidth
                      margin="dense"
                      label="Tiền trước thuế"
                      name="ReiAmountBeforeTax"
                      value={editData?.ReiAmountBeforeTax}
                      onValueChange={(values) => {
                        handleFieldChange({
                          target: {
                            name: "ReiAmountBeforeTax",
                            value: values.value,
                          },
                        });
                      }}
                      slotProps={{
                        input: {
                          readOnly: editData?.ReiStatus === 1 || !uncalculated,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": { textAlign: "right" },
                      }}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowNegative
                      fullWidth
                      margin="dense"
                      label="Tiền thuế"
                      name="ReiVatAmount"
                      value={editData?.ReiVatAmount}
                      onValueChange={(values) => {
                        handleFieldChange({
                          target: {
                            name: "ReiVatAmount",
                            value: values.value,
                          },
                        });
                      }}
                      slotProps={{
                        input: {
                          readOnly: editData?.ReiStatus === 1 || !uncalculated,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": { textAlign: "right" },
                      }}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator="."
                      decimalSeparator=","
                      allowNegative
                      fullWidth
                      margin="dense"
                      label="Tổng tiền"
                      name="ReiAmountAfterTax"
                      value={editData?.ReiAmountAfterTax}
                      onValueChange={(values) => {
                        handleFieldChange({
                          target: {
                            name: "ReiAmountAfterTax",
                            value: values.value,
                          },
                        });
                      }}
                      slotProps={{
                        input: {
                          readOnly: editData?.ReiStatus === 1 || !uncalculated,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": { textAlign: "right" },
                      }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </div>
          <DialogActions>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {/* <div style={{ width: "100%", textAlign: "right" }}>
                <span style={{ color: "red" }}>
                  Vui lòng khóa phiếu khi có thay đổi thông tin hóa đơn hoặc chi
                  tiết mặt hàng để tránh nhận thông tin đồng bộ từ POS bán hàng
                </span>
              </div> */}
              <Grid container spacing={0.8} style={{ justifyContent: "end" }}>
                {isApprove ? (
                  <Button
                    onClick={() => handleApproveInvoice(false, 0, editData)}
                    color="success"
                    variant="contained"
                  >
                    <LockOpenIcon sx={{ mr: 1 }} /> Mở khoá
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleApproveInvoice(true, 1, editData)}
                    color="error"
                    variant="contained"
                  >
                    <LockIcon sx={{ mr: 1 }} /> Khóa
                  </Button>
                )}
                <Button
                  onClick={() => handleGetInvoiceInfor(false)}
                  color="primary"
                  variant="contained"
                  disabled={editData.ReiEStatus === 1}
                >
                  <CachedIcon sx={{ mr: 1 }} />
                  Lấy TTHĐ
                </Button>
                <Button
                  onClick={(e) => handleSaveInvoice(false)}
                  color="primary"
                  variant="contained"
                  disabled={
                    editData.ReiEStatus === 4 || editData.ReiSource === 3
                  }
                >
                  <SaveIcon sx={{ mr: 1 }} /> Lưu
                </Button>
                <Button
                  onClick={(e) => handleSaveInvoice(true)}
                  color="primary"
                  variant="contained"
                  disabled={
                    editData.ReiEStatus === 4 || editData.ReiSource === 3
                  }
                >
                  <SaveIcon sx={{ mr: 1 }} /> Lưu và khóa
                </Button>
                <Button
                  onClick={handleSyncInvoice}
                  color="info"
                  variant="outlined"
                  disabled={
                    editData.ReiEStatus === 4 || editData.ReiSource === 3
                  }
                >
                  <SyncIcon sx={{ mr: 1 }} /> Lưu, Khoá và Đồng bộ
                </Button>
                {(user?.UsEmail === "admini" || user?.UsUserName === "admini") && (
                  <Button
                    onClick={() => handleClearTaxAuthorityCode(editData.ReiId)}
                    color="error"
                    variant="contained"
                    disabled={!editData.ReiId}
                  >
                    <DeleteIcon sx={{ mr: 1 }} /> Xóa Mã CQT
                  </Button>
                )}
              </Grid>
            </div>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
      {/* Form nhập chi tiết mặt hàng (Đang phát triển) */}
      <InvoiceDetailDialog
        open={openRowEditModal}
        onClose={() => setOpenRowEditModal(false)}
      ></InvoiceDetailDialog>
      <ConfirmDialog
        open={confirmDialogState.open}
        title={confirmDialogState.title}
        content={confirmDialogState.content}
        onConfirm={confirmDialogState.onConfirm}
        onClose={confirmDialogState.onClose}
        confirmText={confirmDialogState.confirmText || "Chấp nhận"}
        cancelText={confirmDialogState.cancelText || "Hủy"}
        loading={confirmDialogState.loading || false}
      />
    </>
  );
});

export default InvoiceModal;
